import type {
  ActiveStatusChallengeResponse,
  User,
  UserCreditsSlim,
} from "@/generated/email_parser.openapi";
import {
  CHALLENGE_THRESHOLDS,
  isUserEligibleForStatusChallengeFlow,
  type ChallengeType,
} from "./statusChallengeEligibility";

export type ChallengeUser = Pick<
  User,
  | "activeStatusChallenge"
  | "cashbackPercentage"
  | "hasHistoricalStatusChallenge"
>;

/** Derive the customer challenge state without reading account context or fetching. */
export function deriveStatusChallenge({
  user,
  credits,
  activeChallenge,
  loading = false,
  settled = false,
}: {
  user: ChallengeUser | null | undefined;
  credits: UserCreditsSlim | null | undefined;
  activeChallenge: ActiveStatusChallengeResponse | null;
  loading?: boolean;
  settled?: boolean;
}) {
  const isActive = !!user?.activeStatusChallenge;
  const activeChallengeLoading =
    loading || (isActive && !activeChallenge && !settled);
  const challengeType =
    (activeChallenge?.challengeType as ChallengeType | undefined) ?? null;
  const isSilverOnly =
    !activeChallenge ||
    (activeChallenge.nightsRequiredGold == null &&
      activeChallenge.spendingRequiredGold == null);
  const nightsRequiredSilver = activeChallenge
    ? activeChallenge.nightsRequiredSilver ?? null
    : CHALLENGE_THRESHOLDS.status_challenge_2.nightsRequiredSilver;
  const spendingRequiredSilver = activeChallenge
    ? activeChallenge.spendingRequiredSilver ?? null
    : CHALLENGE_THRESHOLDS.status_challenge_2.spendingRequiredSilver;
  const nightsRequiredGold = activeChallenge?.nightsRequiredGold ?? null;
  const spendingRequiredGold = activeChallenge?.spendingRequiredGold ?? null;
  const nightsCurrent = isActive
    ? credits?.statusChallengeStats?.statusChallengeNights ?? 0
    : 0;
  const spendingCurrent = isActive
    ? credits?.statusChallengeStats?.statusChallengeSpend?.value ?? 0
    : 0;
  const silverCompleted =
    nightsRequiredSilver != null &&
    spendingRequiredSilver != null &&
    (nightsCurrent >= nightsRequiredSilver ||
      spendingCurrent >= spendingRequiredSilver);
  const goldCompleted =
    nightsRequiredGold != null &&
    spendingRequiredGold != null &&
    (nightsCurrent >= nightsRequiredGold ||
      spendingCurrent >= spendingRequiredGold);
  const statusChallengeComplete: "silver" | "gold" | null =
    !isActive || !activeChallenge
      ? null
      : goldCompleted
        ? "gold"
        : silverCompleted
          ? "silver"
          : null;
  return {
    statusChallengeEligible:
      !!user && isUserEligibleForStatusChallengeFlow(user),
    statusChallengeInProgress:
      isActive && !(isSilverOnly ? silverCompleted : goldCompleted),
    statusChallengeComplete,
    activeChallengeLoading,
    challengeType,
    isSilverOnly,
    nightsCurrent,
    spendingCurrent,
    nightsRequiredSilver,
    spendingRequiredSilver,
    nightsRequiredGold,
    spendingRequiredGold,
    bookingDeadline: activeChallenge?.endDate ?? null,
  };
}
export type StatusChallengeState = ReturnType<typeof deriveStatusChallenge>;
